// Word-level diff shared by every before/after view in the app. LCS over word
// tokens; whitespace travels with the preceding word so the rendered segments
// reassemble into the exact original strings.

export type DiffSegment = {
  text: string
  // "equal" appears on both sides, "del" only in the old text, "add" only in
  // the new text.
  op: "equal" | "del" | "add"
}

// Tokens are word + trailing whitespace, so join(tokens) === input.
function tokenize(text: string): string[] {
  return text.match(/\S+\s*|\s+/g) ?? []
}

/**
 * Word-level diff of `oldText` -> `newText` as one segment stream:
 * render segments with op !== "add" for the old side and op !== "del" for
 * the new side. Classic O(n*m) LCS — these are short UI strings.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  const a = tokenize(oldText)
  const b = tokenize(newText)

  const n = a.length
  const m = b.length
  // lcs[i][j] = LCS length of a[i:] vs b[j:].
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    Array.from({ length: m + 1 }, () => 0),
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const segments: DiffSegment[] = []
  const push = (text: string, op: DiffSegment["op"]) => {
    if (text === "") return
    const last = segments.at(-1)
    if (last && last.op === op) last.text += text
    else segments.push({ text, op })
  }

  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push(a[i], "equal")
      i++
      j++
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push(a[i], "del")
      i++
    } else {
      push(b[j], "add")
      j++
    }
  }
  while (i < n) push(a[i++], "del")
  while (j < m) push(b[j++], "add")

  return segments
}

/**
 * The one-segment stream for a value with no counterpart to compare against:
 * a line that was purely added or purely removed, or a field that is equal on
 * both sides. The line still carries its tint, but nothing inside it is
 * word-highlighted — there is no "changed part" to point at.
 */
export function plainSegments(text: string): DiffSegment[] {
  return [{ text, op: "equal" }]
}
